package com.writerslab

import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.client.request.forms.*
import io.ktor.http.*
import kotlinx.coroutines.delay
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement

// coerceInputValues: JSON null в НЕнулевом поле с дефолтом → дефолт, а не
// краш десериализации (страховка под расхождения типов в отчёте AI v2;
// несовпадение тип↔массив оно НЕ чинит — это остаётся на бэкенде).
private val json = Json {
    ignoreUnknownKeys = true
    coerceInputValues = true
}

/** A stylistic-issue span in the manuscript: character offsets into the text
 *  the reader renders, plus the legend group id. */
@Serializable
data class HlSpan(val start: Int, val end: Int, val group: String)

@Serializable
private data class HlSpanResponse(val spans: List<HlSpan> = emptyList())

/** EXPERIMENT — one AI-found stylistic issue: char offsets + catalog-section
 *  group (for legend/colour) + the specific violation type/label and the
 *  verifier's `feature` note and quoted phrase (for the per-span hover).
 *  A finding that partly overlaps another arrives cut into nested pieces:
 *  they share `finding` (the finding's number in the result), are flagged
 *  `part`, and carry the whole finding's quote/feature. Results cached before
 *  the analyzer sent these fields have `finding = -1`: one span = one finding. */
@Serializable
data class AiHlSpan(
    val start: Int, val end: Int, val group: String,
    val type: String = "", val label: String = "",
    val feature: String = "", val quote: String = "",
    val finding: Int = -1, val part: Boolean = false,
    /** Moved onto an edited text by finding its words again — not re-checked. */
    val relocated: Boolean = false,
)

@Serializable
data class AiHlMeta(
    val chars: Int = 0,
    @SerialName("n_confirmed") val confirmed: Int = 0,
    @SerialName("n_located") val located: Int = 0,
    /** Text ranges [start, end] changed since the scan: their marks are not
     *  re-checked until «Проверить правки». */
    val unchecked: List<List<Int>> = emptyList(),
    /** Set by the last re-check of edits: how many findings went / came. */
    val recheck: AiHlRecheck? = null,
)

@Serializable
data class AiHlRecheck(val removed: Int = 0, val added: Int = 0, val chunks: Int = 0)

@Serializable
data class AiHlResponse(val spans: List<AiHlSpan> = emptyList(), val meta: AiHlMeta? = null)

/** Outcome of an AI-highlights request: a cached-or-fresh result, an explicit
 *  "no cache yet" (204 — nothing was computed, nothing charged), or a failure. */
sealed interface AiHlOutcome {
    data class Loaded(val res: AiHlResponse) : AiHlOutcome
    object NoCache : AiHlOutcome
    object Failed : AiHlOutcome
}

/** Static violation-type catalogue for the reader's tree legend: sections →
 *  types. `label` matches the per-span hover label, so a hinted type maps to a
 *  legend leaf. */
@Serializable
data class AiCatType(val id: String, val label: String, val hint: String = "")

@Serializable
data class AiCatSection(val id: String, val label: String, val types: List<AiCatType> = emptyList())

@Serializable
data class AiCatalog(val sections: List<AiCatSection> = emptyList())

@Serializable
data class UserInfo(
    val id: String = "",
    val email: String? = null,
    val name: String? = null,
    val credits: Int = 0,
    val debugTopUp: Boolean = false,
    /** Free AI runs left today on very short texts (server-side daily
     *  reset, UTC). Logged-in users get N/day on samples below the
     *  short-text threshold. */
    val freeShortRemaining: Int = 0,
    /** Character-count cutoff for "very short text" eligibility.
     *  Mirrored from the server so the threshold stays in one place.
     *  Chars not words — a single whitespace-free «word» of arbitrary
     *  length must not slip past the cap. */
    val freeShortCharThreshold: Int = 30000,
    /** Lifetime free pool remaining for annotation generations
     *  (Pricing.FREE_ANNOTATIONS_PER_USER minus used). 0 → annotation
     *  is paid. */
    val freeAnnotationsRemaining: Int = 0,
    /** Lifetime free pool remaining for full cover-batch generations
     *  (Pricing.FREE_COVER_BATCHES_PER_USER minus used). Drives the
     *  Cover-tab CTA price tag — 0 free remaining → batch is paid. */
    val freeCoverBatchesRemaining: Int = 0,
    /** Lifetime free pool remaining for teaser scene-preview
     *  generations. Drives the Teaser-tab preview CTA price tag. */
    val freeTeaserPreviewsRemaining: Int = 0,
    /** Lifetime free pool remaining for AI-highlight previews
     *  (first 30k). 0 → the preview button costs 1 credit. */
    val freeAiHighlightPreviewsRemaining: Int = 0,
    /** May edit book texts in the reader: bought credits at least once, or
     *  switched on in the office. */
    val canEditText: Boolean = false,
    val emailVerified: Boolean = false,
    val verificationEmailSentAt: Long? = null,
)

@Serializable
data class RedeemRequest(val code: String)

/** What re-checking the edited places costs now (GET …/ai-highlights/recheck). */
@Serializable
data class RecheckEstimate(
    val scope: String = "full",
    val chunks: Int = 0,
    val chars: Int = 0,
    val uncachedChars: Int = 0,
    val cost: Int = 0,
    /** A free preview slot pays for it instead of credits. */
    val freePreview: Boolean = false,
)

sealed interface RecheckOutcome {
    data class Done(val res: AiHlResponse) : RecheckOutcome
    /** The price moved since it was shown (another save, a free slot used up). */
    data class PriceChanged(val price: RecheckEstimate) : RecheckOutcome
    /** Not enough credits for the shown price. */
    object NoCredits : RecheckOutcome
    data class Failed(val reason: String) : RecheckOutcome
}

@Serializable
private data class RecheckError(val error: String? = null, val cost: Int? = null, val scope: String = "full",
                                val chunks: Int = 0, val chars: Int = 0, val uncachedChars: Int = 0,
                                val freePreview: Boolean = false)

/** The book's text and its revision (SHA-256) — what a reader edit starts from. */
data class BookText(val text: String, val sha: String?)

@Serializable
private data class SaveTextRequest(val baseSha: String, val paragraphs: List<String>)

@Serializable
private data class TextEditResponse(
    val sha: String = "",
    val analysis: JsonElement? = null,
    val textEditedAt: String? = null,
    val marksDropped: Int? = null,
    val reportTextChanged: Boolean = false,
    val error: String? = null,
    val words: Int? = null,
)

/** Outcome of saving the text edited in the reader. */
sealed interface TextEditOutcome {
    data class Saved(
        val sha: String,
        val analysisJson: String?,
        val analysis: AnalysisResult?,
        /** When the text was last edited. */
        val editedAt: String?,
        /** Smart marks whose words are gone from the edited places. */
        val marksDropped: Int,
        /** The deep-analysis report was made from another text. */
        val reportTextChanged: Boolean,
    ) : TextEditOutcome
    /** "text_changed" (saved from another tab), "scan_running", "book_busy". */
    data class Conflict(val reason: String) : TextEditOutcome
    data class TooShort(val words: Int) : TextEditOutcome
    object NotAllowed : TextEditOutcome
    object Failed : TextEditOutcome
}

@Serializable
data class CheckoutRequest(val packId: String, val region: String)

@Serializable
data class CheckoutResponse(
    val paymentId: String? = null,
    val redirectUrl: String? = null,
    val error: String? = null,
)

@Serializable
data class PaymentStatusResponse(
    /** "PENDING" / "CONFIRMED" / "CANCELED" / "CHARGEBACK". */
    val status: String = "",
    /** Post-grant credit balance, refreshed at every poll. */
    val credits: Int = 0,
    /** Credits added by this specific payment — 0 unless status is
     *  CONFIRMED. Used to surface the right number in the success toast. */
    val creditsAdded: Int = 0,
    val packId: String = "",
)

@Serializable
data class PurchaseEventsClaimResponse(val paymentIds: List<String> = emptyList())

sealed class CheckoutResult {
    data class Redirect(val paymentId: String, val url: String) : CheckoutResult()
    data class Failure(val error: String) : CheckoutResult()
}

sealed class PaymentStatusResult {
    data class Known(val status: PaymentStatusResponse) : PaymentStatusResult()
    /** 404 — no such payment for this user; stop asking about it. */
    object NotFound : PaymentStatusResult()
    /** Network error, 5xx, or signed out — worth asking again later. */
    object Failed : PaymentStatusResult()
}

@Serializable
data class RedeemResponse(
    val credits: Int? = null,
    val added: Int? = null,
    val error: String? = null,
)

sealed class RedeemResult {
    data class Success(val credits: Int, val added: Int) : RedeemResult()
    data class Failure(val error: String) : RedeemResult()
}

sealed class VerificationEmailResult {
    object Sent : VerificationEmailResult()
    object AlreadyVerified : VerificationEmailResult()
    data class Cooldown(val retryAfterSeconds: Int) : VerificationEmailResult()
    object Error : VerificationEmailResult()
}

/**
 * Repository for all backend API calls.
 */
object BookRepository {

    private val baseUrl get() = jsApiBase()

    /**
     * Fetch current user info. The Bearer auth plugin attaches the access
     * token automatically and refreshes on 401.
     */
    suspend fun fetchUserInfo(): UserInfo? {
        if (!Auth.isLoggedIn) return null
        return try {
            val response = httpClient.get("$baseUrl/api/user/me")
            response.body<UserInfo>()
        } catch (e: Throwable) {
            if (e is kotlinx.coroutines.CancellationException) throw e
            null
        }
    }

    /**
     * Top up credits. Returns new credit count (debug only).
     */
    suspend fun topUpCredits(amount: Int = 10): Int {
        if (!Auth.isLoggedIn) return 0
        val response = httpClient.post("$baseUrl/api/user/credits/topup?amount=$amount")
        val body = response.body<String>()
        val credits = json.decodeFromString<Map<String, Int>>(body)
        return credits["credits"] ?: 0
    }

    /**
     * Redeem a credit code. Returns the new balance and amount added on success,
     * or a normalized error string ("invalid_format" / "invalid_or_used" / "network").
     */
    suspend fun redeemCode(code: String): RedeemResult {
        if (!Auth.isLoggedIn) return RedeemResult.Failure("network")
        return try {
            val payload = Json.encodeToString(RedeemRequest.serializer(), RedeemRequest(code))
            val response = httpClient.post("$baseUrl/api/user/credits/redeem") {
                headers { append(HttpHeaders.ContentType, "application/json") }
                setBody(payload)
            }
            val body = response.body<String>()
            val parsed = json.decodeFromString<RedeemResponse>(body)
            if (parsed.error != null) {
                RedeemResult.Failure(parsed.error)
            } else if (parsed.credits != null && parsed.added != null) {
                RedeemResult.Success(parsed.credits, parsed.added)
            } else {
                RedeemResult.Failure("network")
            }
        } catch (e: Throwable) {
            if (e is kotlinx.coroutines.CancellationException) throw e
            RedeemResult.Failure("network")
        }
    }

    suspend fun sendVerificationEmail(): VerificationEmailResult {
        return try {
            val response = httpClient.post("$baseUrl/api/user/send-verification-email")
            val body = response.body<Map<String, String>>()
            if (body["status"] == "already_verified") VerificationEmailResult.AlreadyVerified
            else VerificationEmailResult.Sent
        } catch (e: io.ktor.client.plugins.ClientRequestException) {
            if (e.response.status.value == 429) {
                val body = runCatching { e.response.body<Map<String, Int>>() }.getOrNull()
                VerificationEmailResult.Cooldown(body?.get("retryAfterSeconds") ?: 60)
            } else {
                VerificationEmailResult.Error
            }
        } catch (e: Throwable) {
            if (e is kotlinx.coroutines.CancellationException) throw e
            VerificationEmailResult.Error
        }
    }

    /**
     * Kick off a real-money top-up. Backend creates a PENDING payment
     * row (or hands back a still-open one for the same pack), asks the
     * provider for a hosted checkout URL, and returns it. Caller is
     * expected to navigate the browser to the URL — the user pays on the
     * provider's page and is redirected back to `/?payment=<id>`.
     */
    suspend fun startCheckout(packId: String, region: String): CheckoutResult {
        if (!Auth.isLoggedIn) return CheckoutResult.Failure("not_logged_in")
        return try {
            val payload = Json.encodeToString(
                CheckoutRequest.serializer(),
                CheckoutRequest(packId, region),
            )
            val response = httpClient.post("$baseUrl/api/payments/checkout") {
                headers { append(HttpHeaders.ContentType, "application/json") }
                setBody(payload)
            }
            val body = response.body<String>()
            val parsed = json.decodeFromString<CheckoutResponse>(body)
            when {
                parsed.error != null -> CheckoutResult.Failure(parsed.error)
                parsed.paymentId != null && parsed.redirectUrl != null ->
                    CheckoutResult.Redirect(parsed.paymentId, parsed.redirectUrl)
                else -> CheckoutResult.Failure("network")
            }
        } catch (e: kotlinx.coroutines.CancellationException) {
            throw e
        } catch (_: Throwable) {
            // Throwable, not Exception: Ktor's JS engine reports a failed
            // fetch (offline, dropped connection) as kotlin.Error.
            CheckoutResult.Failure("network")
        }
    }

    /** One-shot status fetch for a previously-created payment. */
    suspend fun getPaymentStatus(paymentId: String): PaymentStatusResult {
        if (!Auth.isLoggedIn) return PaymentStatusResult.Failed
        return try {
            val response = httpClient.get("$baseUrl/api/payments/$paymentId/status")
            when (response.status.value) {
                200 -> PaymentStatusResult.Known(
                    json.decodeFromString<PaymentStatusResponse>(response.body<String>())
                )
                404 -> PaymentStatusResult.NotFound
                else -> PaymentStatusResult.Failed
            }
        } catch (e: kotlinx.coroutines.CancellationException) {
            throw e
        } catch (_: Throwable) {
            // Throwable, not Exception: a failed fetch arrives as kotlin.Error.
            PaymentStatusResult.Failed
        }
    }

    /**
     * Take the ids of confirmed payments whose GTM `purchase` hasn't been
     * pushed yet; the backend marks them sent as it returns them. Empty on
     * any failure — analytics must never break the app.
     */
    suspend fun claimPurchaseEvents(): List<String> {
        if (!Auth.isLoggedIn) return emptyList()
        return try {
            val response = httpClient.post("$baseUrl/api/payments/purchase-events/claim")
            if (response.status.value != 200) return emptyList()
            json.decodeFromString<PurchaseEventsClaimResponse>(response.body<String>()).paymentIds
        } catch (e: Throwable) {
            if (e is kotlinx.coroutines.CancellationException) throw e
            emptyList()
        }
    }

    /**
     * Fetch cached static analysis by bookId.
     */
    suspend fun getCachedAnalysis(bookId: String): AnalysisResult? {
        if (!Auth.isLoggedIn) return null
        return try {
            val response = httpClient.get("$baseUrl/api/books/analyze/cached/$bookId")
            if (response.status.value != 200) return null
            val body = response.body<String>()
            lastAnalysisJson = body
            val apiResponse = json.decodeFromString<ApiAnalysisResponse>(body)
            apiResponse.toAnalysisResult()
        } catch (e: Throwable) {
            if (e is kotlinx.coroutines.CancellationException) throw e
            null
        }
    }

    /** Attach the anon read-capability token for [bookId] when we hold one, so
     *  an anonymous reader can fetch this book's text/highlights. Harmless once
     *  logged in — the backend prefers the JWT owner check and ignores it. */
    private fun HttpRequestBuilder.anonBookToken(bookId: String) {
        AppDataStore.getAnonReadToken(bookId)?.let { header("X-Book-Token", it) }
    }

    /** Short prefix of the extracted text for the given book. */
    suspend fun getTextSample(bookId: String): String? = try {
        val response = httpClient.get("$baseUrl/api/books/text/$bookId/sample") { anonBookToken(bookId) }
        if (response.status.value != 200) null
        else json.decodeFromString<Map<String, String>>(response.body<String>())["sample"]
    } catch (e: Throwable) {
        if (e is kotlinx.coroutines.CancellationException) throw e
        null
    }

    /** Full extracted text of the book — the same `book.txt` content the .txt
     *  download serves (via [downloadBookTxt]), returned as a string instead of
     *  triggering a download. Access-gated server-side (JWT owner or anon
     *  token); null if unavailable. */
    suspend fun getBookText(bookId: String): String? = try {
        val response = httpClient.get("$baseUrl/api/books/text/$bookId") { anonBookToken(bookId) }
        if (response.status.value != 200) null else response.body<String>()
    } catch (e: Throwable) {
        if (e is kotlinx.coroutines.CancellationException) throw e
        null
    }

    /** [getBookText] plus the text revision from the `X-Text-Sha` header. */
    suspend fun getBookTextWithSha(bookId: String): BookText? = try {
        val response = httpClient.get("$baseUrl/api/books/text/$bookId") { anonBookToken(bookId) }
        if (response.status.value != 200) null
        else BookText(response.body<String>(), response.headers["X-Text-Sha"])
    } catch (e: kotlinx.coroutines.CancellationException) {
        throw e
    } catch (_: Throwable) {
        null
    }

    /** Save the reader's paragraphs as the book's text, starting from revision
     *  [baseSha]. The server keeps untouched lines as they were. */
    suspend fun saveBookText(bookId: String, baseSha: String, paragraphs: List<String>): TextEditOutcome =
        sendTextEdit {
            httpClient.put("$baseUrl/api/books/text/$bookId") {
                headers { append(HttpHeaders.ContentType, "application/json") }
                setBody(json.encodeToString(SaveTextRequest.serializer(), SaveTextRequest(baseSha, paragraphs)))
            }
        }

    // Ktor's JS engine reports a failed fetch as kotlin.Error, so catch Throwable.
    private suspend fun sendTextEdit(send: suspend () -> io.ktor.client.statement.HttpResponse): TextEditOutcome = try {
        val response = send()
        val parsed = runCatching { json.decodeFromString<TextEditResponse>(response.body<String>()) }.getOrNull()
        when (response.status.value) {
            200 -> {
                val raw = parsed?.analysis?.toString()
                val analysis = raw?.let {
                    runCatching { json.decodeFromString<ApiAnalysisResponse>(it).toAnalysisResult() }.getOrNull()
                }
                TextEditOutcome.Saved(
                    sha = parsed?.sha.orEmpty(),
                    analysisJson = raw,
                    analysis = analysis,
                    editedAt = parsed?.textEditedAt,
                    marksDropped = parsed?.marksDropped ?: 0,
                    reportTextChanged = parsed?.reportTextChanged ?: false,
                )
            }
            409 -> TextEditOutcome.Conflict(parsed?.error ?: "text_changed")
            422 -> TextEditOutcome.TooShort(parsed?.words ?: 0)
            403 -> if (parsed?.error == "edit_not_allowed") TextEditOutcome.NotAllowed else TextEditOutcome.Failed
            else -> TextEditOutcome.Failed
        }
    } catch (e: kotlinx.coroutines.CancellationException) {
        throw e
    } catch (_: Throwable) {
        TextEditOutcome.Failed
    }

    /** Price of re-checking the edited places now; null when there's nothing
     *  to re-check or the server can't say. */
    suspend fun getRecheckEstimate(bookId: String): RecheckEstimate? = try {
        val response = httpClient.get("$baseUrl/api/books/text/$bookId/ai-highlights/recheck")
        if (response.status.value != 200) null
        else json.decodeFromString<RecheckEstimate>(response.body<String>())
    } catch (e: kotlinx.coroutines.CancellationException) {
        throw e
    } catch (_: Throwable) {
        null
    }

    /** Re-check the edited places at the [cost] the user was shown, and wait
     *  for the merged {spans, meta} (a batch run takes minutes). */
    suspend fun runRecheck(bookId: String, cost: Int, freePreview: Boolean): RecheckOutcome {
        val jobId = try {
            val response = httpClient.post(
                "$baseUrl/api/books/text/$bookId/ai-highlights/recheck?cost=$cost&free=$freePreview")
            val body = response.body<String>()
            when (response.status.value) {
                in 200..299 -> json.decodeFromString<PromoJobDto>(body).jobId
                    ?: return RecheckOutcome.Failed("no_job")
                402 -> return RecheckOutcome.NoCredits
                else -> {
                    val err = runCatching { json.decodeFromString<RecheckError>(body) }.getOrNull()
                    if (err?.error == "price_changed" && err.cost != null) {
                        return RecheckOutcome.PriceChanged(RecheckEstimate(err.scope, err.chunks, err.chars,
                            err.uncachedChars, err.cost, err.freePreview))
                    }
                    return RecheckOutcome.Failed(err?.error ?: "http_${response.status.value}")
                }
            }
        } catch (e: kotlinx.coroutines.CancellationException) {
            throw e
        } catch (_: Throwable) {
            return RecheckOutcome.Failed("network")
        }
        return awaitRecheck(bookId, jobId)
    }

    /** Wait for a running re-check job (also after a page reload). */
    suspend fun awaitRecheck(bookId: String, jobId: String): RecheckOutcome = try {
        RecheckOutcome.Done(awaitAndDecode(jobId, AiHlResponse.serializer(), "Re-check"))
    } catch (e: kotlinx.coroutines.CancellationException) {
        throw e
    } catch (e: Throwable) {
        // The server forgot the job (a restart, or it ended while this tab
        // slept): a re-check that went through left nothing unchecked on disk.
        val onDisk = listOf("full", "preview").firstNotNullOfOrNull { sc ->
            (getAiHighlights(bookId, sc, compute = false, force = false) as? AiHlOutcome.Loaded)?.res
                ?.takeIf { it.meta?.recheck != null && it.meta.unchecked.isEmpty() }
        }
        if (onDisk != null) RecheckOutcome.Done(onDisk) else RecheckOutcome.Failed(e.message ?: "failed")
    }

    /** Stylistic-issue spans for the manuscript reader (char offsets + group).
     *  Best-effort: null on any failure, so the reader still shows plain text. */
    suspend fun getHighlights(bookId: String): List<HlSpan>? = try {
        val response = httpClient.get("$baseUrl/api/books/text/$bookId/highlights") { anonBookToken(bookId) }
        if (response.status.value != 200) null
        else json.decodeFromString<HlSpanResponse>(response.body<String>()).spans
    } catch (e: Throwable) {
        if (e is kotlinx.coroutines.CancellationException) throw e
        null
    }

    /** AI-based stylistic-issue spans, cached per scope in the book's shared
     *  folder. `scope` = "preview" (first 30k) or "full" (whole book).
     *  `compute=false` only reads the cache (free → NoCache when absent);
     *  `compute=true` runs the PAID preview scan on a miss (full scan is async —
     *  see [runAiHighlightsFull]); `force=true` re-runs regardless. */
    suspend fun getAiHighlights(
        bookId: String, scope: String = "preview",
        compute: Boolean, force: Boolean,
    ): AiHlOutcome = try {
        val response = httpClient.post(
            "$baseUrl/api/books/text/$bookId/ai-highlights?scope=$scope&compute=$compute&force=$force"
        )
        when (response.status.value) {
            200 -> AiHlOutcome.Loaded(json.decodeFromString<AiHlResponse>(response.body<String>()))
            204 -> AiHlOutcome.NoCache
            else -> AiHlOutcome.Failed
        }
    } catch (e: Throwable) {
        if (e is kotlinx.coroutines.CancellationException) throw e
        AiHlOutcome.Failed
    }

    /** Start the async FULL-manuscript AI-highlight scan (whole book, run as a
     *  batch: minutes to an hour), poll to completion, and decode the
     *  {spans, meta} result. Throws on failure — credits are refunded
     *  server-side, so the caller just shows the error and can retry. */
    suspend fun runAiHighlightsFull(bookId: String, force: Boolean = false): AiHlResponse {
        val jobId = startPromoJob(
            "$baseUrl/api/books/text/$bookId/ai-highlights/start?force=$force",
            errorLabel = "AI highlights",
        )
        return awaitAiHighlightsFull(jobId)
    }

    /** Re-attach to an already-running full scan (discovered via
     *  [getActivePromoJobs] on book open) and await its {spans, meta} result. */
    suspend fun awaitAiHighlightsFull(jobId: String): AiHlResponse =
        awaitAndDecode(jobId, AiHlResponse.serializer(), "AI highlights")

    private var cachedCatalog: AiCatalog? = null

    /** The static violation-type catalogue (sections → types) for the tree
     *  legend. Fetched once per session and memoised (it never changes). */
    suspend fun getAiCatalog(): AiCatalog? {
        cachedCatalog?.let { return it }
        return try {
            val r = httpClient.get("$baseUrl/api/books/ai-highlights/catalog")
            if (r.status.value != 200) null
            else json.decodeFromString<AiCatalog>(r.body<String>()).also { cachedCatalog = it }
        } catch (e: Throwable) {
            if (e is kotlinx.coroutines.CancellationException) throw e
            null
        }
    }

    /**
     * Download the cached extracted text as a .txt file. The endpoint requires
     * ownership, so we go through httpClient (Bearer auth + refresh) rather
     * than a plain browser fetch.
     */
    suspend fun downloadBookTxt(bookId: String, filename: String) {
        if (!Auth.isLoggedIn) return
        try {
            val response = httpClient.get("$baseUrl/api/books/text/$bookId")
            if (response.status.value != 200) return
            jsTriggerTextDownload(response.body<String>(), filename)
        } catch (e: Throwable) {
            if (e is kotlinx.coroutines.CancellationException) throw e
        }
    }

    /**
     * Get user's book history.
     */
    suspend fun getBookHistory(): List<BookHistoryItem> {
        if (!Auth.isLoggedIn) return emptyList()
        return try {
            val response = httpClient.get("$baseUrl/api/books/history")
            json.decodeFromString<List<BookHistoryItem>>(response.body<String>())
        } catch (e: Throwable) {
            if (e is kotlinx.coroutines.CancellationException) throw e
            emptyList()
        }
    }

    /**
     * Upload file and run static analysis.
     * Returns analysis result with bookId for later pipeline use.
     */
    suspend fun analyzeFile(bytes: ByteArray, fileName: String): Pair<AnalysisResult, String> {
        val response = httpClient.submitFormWithBinaryData(
            url = "$baseUrl/api/books/analyze",
            formData = formData {
                append("file", bytes, Headers.build {
                    append(HttpHeaders.ContentDisposition, "filename=\"$fileName\"")
                    append(HttpHeaders.ContentType, "application/octet-stream")
                })
            }
        )

        val body = response.body<String>()
        if (body.isBlank()) {
            throw RuntimeException("Empty response from server (HTTP ${response.status.value})")
        }
        val errorCheck = json.decodeFromString<ApiErrorResponse>(body)
        if (!errorCheck.error.isNullOrEmpty()) {
            throw RuntimeException(errorCheck.error)
        }

        val apiResponse = json.decodeFromString<ApiAnalysisResponse>(body)
        lastAnalysisJson = body
        // Anon uploads come back with a per-book read token; stash it so the
        // reader can fetch text/highlights without a login (see AppDataStore
        // and [anonBookToken]). Empty for logged-in uploads.
        if (apiResponse.anon_read_token.isNotEmpty()) {
            AppDataStore.setAnonReadToken(apiResponse.book_id, apiResponse.anon_read_token)
        }
        return apiResponse.toAnalysisResult() to apiResponse.book_id
    }

    /**
     * Sign-in handoff: copy the per-fingerprint anon data into the
     * freshly-signed-in user's own dir on the backend. Idempotent —
     * returns false silently if there's nothing to claim or if the
     * user already has data for this book. Network errors are
     * swallowed; the worst case is the user re-uploads.
     */
    suspend fun claimAnonBook(bookId: String): Boolean {
        if (!Auth.isLoggedIn) return false
        return try {
            val response = httpClient.post("$baseUrl/api/books/claim/$bookId")
            if (response.status.value != 200) return false
            json.decodeFromString<Map<String, Boolean>>(response.body<String>())["claimed"] ?: false
        } catch (e: Throwable) {
            if (e is kotlinx.coroutines.CancellationException) throw e
            false
        }
    }

    /** Raw JSON of last analysis — for saving to sessionStorage */
    var lastAnalysisJson: String? = null
        private set

    fun parseAnalysisJson(jsonStr: String): AnalysisResult {
        val apiResponse = json.decodeFromString<ApiAnalysisResponse>(jsonStr)
        return apiResponse.toAnalysisResult()
    }

    /**
     * Start AI pipeline using previously analyzed book.
     * `lang` selects the LLM output language; defaults to current UI locale.
     */
    suspend fun startPipeline(bookId: String, lang: String = appLocaleSnapshot()): String {
        val response = httpClient.post("$baseUrl/api/books/pipeline/start/$bookId?lang=$lang")
        val body = response.body<String>()

        val result = json.decodeFromString<PipelineStartResponse>(body)
        if (result.error != null) {
            throw RuntimeException(result.error)
        }
        return result.job_id ?: throw RuntimeException("No job_id in response")
    }

    /**
     * Poll pipeline status.
     */
    suspend fun getPipelineStatus(jobId: String): PipelineStatusResponse {
        val response = httpClient.get("$baseUrl/api/books/pipeline/status/$jobId")
        return response.body<PipelineStatusResponse>()
    }

    /**
     * Get pipeline result (AI report). This GET is free — it only reads a
     * report the user has already paid for — so callers may retry it at will.
     *
     * Returns null on the two honest "there is no report here" answers: 404
     * (nothing cached) and 410 (the run failed, and the backend already
     * refunded it). Every other non-200 throws: the report may well exist and
     * be paid for, and a caller that folds a 5xx into "nothing cached" would
     * offer the user a fresh (charged, `force=true`) run for a report they
     * already own. Same null-or-throw split as [getAnnotation].
     */
    suspend fun getPipelineResult(jobId: String): AiReport? {
        val response = httpClient.get("$baseUrl/api/books/pipeline/result/$jobId")
        if (response.status.value == 404 || response.status.value == 410) return null
        val body = response.body<String>()
        if (response.status.value != 200) {
            val err = runCatching { json.decodeFromString<ApiErrorResponse>(body).error }.getOrNull()
            throw RuntimeException(err ?: "Report fetch failed (HTTP ${response.status.value})")
        }

        val errorCheck = json.decodeFromString<ApiErrorResponse>(body)
        if (!errorCheck.error.isNullOrEmpty()) {
            throw RuntimeException(errorCheck.error)
        }

        return json.decodeFromString<AiReport>(body)
    }

    /**
     * Start pipeline and poll until complete.
     * Backend handles caching transparently — if result is cached,
     * status will immediately return "done".
     */
    data class PipelineResult(val report: AiReport, val remainingCredits: Int?)

    suspend fun runPipeline(
        bookId: String,
        onStarted: (remainingCredits: Int?) -> Unit = {},
        onProgress: (status: PipelineStatusResponse) -> Unit,
    ): PipelineResult {
        val lang = appLocaleSnapshot()
        val startResponse = httpClient.post("$baseUrl/api/books/pipeline/start/$bookId?force=true&lang=$lang")
        val startBody = json.decodeFromString<PipelineStartResponse>(startResponse.body<String>())
        if (startBody.error != null) throw RuntimeException(startBody.error)
        val jobId = startBody.job_id ?: throw RuntimeException("No job_id")
        // Backend deducts credits atomically inside /pipeline/start and
        // returns the post-deduction balance. Surface it now so the UI
        // updates immediately on click instead of at end-of-pipeline.
        onStarted(startBody.credits)
        return pollUntilDone(jobId, startBody.credits, onProgress)
    }

    /**
     * Re-attach to an existing pipeline run (e.g. after a page reload). The
     * caller passes the analyzer UUID it learned from `/history`. Returns
     * the final report when the pipeline finishes; throws on error.
     */
    suspend fun resumePipeline(
        jobId: String,
        onProgress: (status: PipelineStatusResponse) -> Unit
    ): PipelineResult = pollUntilDone(jobId, null, onProgress)

    /**
     * Fetch a previously generated promo annotation for a book. Returns
     * null when nothing's cached (404) — callers should treat that as
     * "not generated yet", not an error.
     */
    suspend fun getAnnotation(bookId: String): AnnotationResult? {
        val response = httpClient.get("$baseUrl/api/books/promo/annotation/$bookId")
        if (response.status.value == 404) return null
        if (response.status.value != 200) {
            throw RuntimeException("Annotation fetch failed (HTTP ${response.status.value})")
        }
        return json.decodeFromString<AnnotationResult>(response.body<String>())
    }

    /**
     * Trigger annotation generation. The backend now runs it as a
     * registered job (202 Accepted with jobId) and we poll the job
     * status endpoint until terminal. From the caller's perspective
     * the suspend signature still hides the polling — they get an
     * `AnnotationResult` or an exception just like before.
     *
     * `onJobStarted` fires the moment the backend confirms a jobId.
     * The VM uses it to record «this kind of generation is running
     * on this book, here's the jobId», so a book switch can re-attach
     * via [awaitAnnotationJob] when the user returns.
     */
    suspend fun generateAnnotation(
        bookId: String,
        onJobStarted: (jobId: String) -> Unit = {},
    ): AnnotationResult {
        val lang = appLocaleSnapshot()
        val response = httpClient.post("$baseUrl/api/books/promo/annotation/$bookId?lang=$lang")
        val startBody = response.body<String>()
        if (response.status.value !in 200..299) {
            val err = runCatching { json.decodeFromString<ApiErrorResponse>(startBody).error }.getOrNull()
            throw RuntimeException(err ?: "Annotation generation failed (HTTP ${response.status.value})")
        }
        val job = json.decodeFromString<PromoJobDto>(startBody)
        val jobId = job.jobId ?: throw RuntimeException("Annotation start returned no jobId")
        onJobStarted(jobId)
        return awaitAnnotationJob(jobId)
    }

    /**
     * Poll an already-started annotation job until terminal. Used both
     * by [generateAnnotation] internally and by the VM's re-attach
     * path when the user navigates back to a book whose annotation
     * generation is still running.
     */
    suspend fun awaitAnnotationJob(jobId: String): AnnotationResult =
        awaitAndDecode(jobId, AnnotationResult.serializer(), "Annotation")

    /**
     * Fetch a previously generated cover (variant N). 404 when nothing
     * cached for that variant — caller should treat as "not generated
     * yet", not an error.
     */
    /** Fetch every cached cover variant for a book. Returns the list
     *  (possibly empty) — never throws on missing data. */
    suspend fun getCovers(bookId: String): List<CoverResult> {
        val response = httpClient.get("$baseUrl/api/books/promo/cover/$bookId")
        if (response.status.value != 200) {
            throw RuntimeException("Cover fetch failed (HTTP ${response.status.value})")
        }
        return json.decodeFromString<CoverListResponse>(response.body<String>()).covers
    }

    /**
     * Atomic batch generation: one server-side job runs Sonnet prompt
     * + 3 Gemini renders (~90 sec total). `onJobStarted` fires when
     * the backend confirms the jobId so the VM can record it for
     * re-attach on book switch.
     */
    suspend fun generateCoverBatch(
        bookId: String,
        req: CoverRequest,
        onJobStarted: (jobId: String) -> Unit = {},
    ): List<CoverResult> {
        val jobId = startPromoJob(
            url = "$baseUrl/api/books/promo/cover/$bookId/batch",
            jsonBody = json.encodeToString(CoverRequest.serializer(), req),
            errorLabel = "Cover batch",
        )
        onJobStarted(jobId)
        return awaitCoverBatchJob(jobId)
    }

    suspend fun awaitCoverBatchJob(jobId: String): List<CoverResult> =
        awaitAndDecode(jobId, CoverBatchResponse.serializer(), "Cover batch").covers

    /**
     * Per-slot regeneration: keeps the existing `cover_prompt.json`,
     * runs one Gemini render. Different slots can run concurrently —
     * the backend's `PromoJobKind.COVER_REGEN + variant` key allows
     * up to 3 jobs in parallel per book.
     */
    suspend fun regenerateCoverVariant(
        bookId: String,
        variant: Int,
        req: CoverRequest,
        onJobStarted: (jobId: String) -> Unit = {},
    ): CoverResult {
        val jobId = startPromoJob(
            url = "$baseUrl/api/books/promo/cover/$bookId/regen/$variant",
            jsonBody = json.encodeToString(CoverRequest.serializer(), req),
            errorLabel = "Cover regen",
        )
        onJobStarted(jobId)
        return awaitCoverRegenJob(jobId)
    }

    suspend fun awaitCoverRegenJob(jobId: String): CoverResult =
        awaitAndDecode(jobId, CoverResult.serializer(), "Cover regen")

    /**
     * Fetch public credit pricing for every paid action. Called once on
     * app bootstrap; result is provided to the UI tree via LocalPricing
     * so each CTA's "· N кр" tag and the teaser breakdown stay in sync
     * with whatever the backend Pricing object currently says. Falls
     * back to client-side defaults if the call fails — UI still renders.
     */
    suspend fun getPricing(): Pricing {
        val response = httpClient.get("$baseUrl/api/pricing")
        if (response.status.value != 200) {
            throw RuntimeException("Pricing fetch failed (HTTP ${response.status.value})")
        }
        return json.decodeFromString<Pricing>(response.body<String>())
    }

    /**
     * Fetch any cached teaser for a book (keyframe PNG + optional MP4).
     * Returns an empty TeaserResult (frame_base64=null) if nothing has
     * been generated yet — caller treats that as "Idle". Cheap, just
     * file IO + base64 on the backend.
     */
    suspend fun getTeaser(bookId: String): TeaserResult {
        val response = httpClient.get("$baseUrl/api/books/promo/teaser/$bookId")
        if (response.status.value != 200) {
            throw RuntimeException("Teaser fetch failed (HTTP ${response.status.value})")
        }
        return json.decodeFromString<TeaserResult>(response.body<String>())
    }

    /**
     * Fetch the teaser MP4 as raw bytes via the JWT-authenticated
     * endpoint. Bytes are wrapped in a Blob on the client and either
     * played back through a Blob URL or downloaded — the asset URL
     * itself never reaches the DOM.
     *
     * `version` is a stable discriminator (typically teaser.hashCode())
     * that changes whenever the underlying asset is regenerated. It
     * gets stitched into the URL as `?v=…` purely to defeat browser
     * HTTP-cache reuse across regenerations — same version reuses
     * the cached response, different version forces a fresh fetch.
     */
    suspend fun fetchTeaserVideoBytes(bookId: String, version: String): ByteArray? =
        fetchTeaserAssetBytes("$baseUrl/api/books/promo/teaser/$bookId/video.mp4?v=$version")

    /** Same Bearer-authed fetch as `fetchTeaserVideoBytes` but for the
     *  keyframe PNG. Used by the in-page preview, not for downloads. */
    suspend fun fetchTeaserFrameBytes(bookId: String, version: String): ByteArray? =
        fetchTeaserAssetBytes("$baseUrl/api/books/promo/teaser/$bookId/frame.png?v=$version")

    private suspend fun fetchTeaserAssetBytes(url: String): ByteArray? {
        if (!Auth.isLoggedIn) return null
        return try {
            val response = httpClient.get(url)
            if (response.status.value != 200) return null
            response.body<ByteArray>()
        } catch (e: Throwable) {
            if (e is kotlinx.coroutines.CancellationException) throw e
            null
        }
    }

    /**
     * Trigger teaser generation. Backend job runs ~30s for keyframe-
     * only, ~2 min for the full keyframe + i2v render.
     */
    suspend fun generateTeaser(
        bookId: String,
        req: TeaserRequest,
        onJobStarted: (jobId: String) -> Unit = {},
    ): TeaserResult {
        // Stamp current UI locale if the caller didn't set one — single
        // place to inject, so every call site doesn't have to remember.
        val finalReq = if (req.lang.isBlank()) req.copy(lang = appLocaleSnapshot()) else req
        val jobId = startPromoJob(
            url = "$baseUrl/api/books/promo/teaser/$bookId",
            jsonBody = json.encodeToString(TeaserRequest.serializer(), finalReq),
            errorLabel = "Teaser generation",
        )
        onJobStarted(jobId)
        return awaitTeaserJob(jobId)
    }

    suspend fun awaitTeaserJob(jobId: String): TeaserResult =
        awaitAndDecode(jobId, TeaserResult.serializer(), "Teaser")

    /**
     * The «which generations are currently running on this book?» probe.
     * Called after `selectFromHistory` finishes loading the static
     * analysis, so the VM can resubscribe loaders to anything that
     * was kicked off in a previous session / tab. Returns an empty
     * list when nothing is running (treated as «idle» by the VM).
     */
    suspend fun getActivePromoJobs(bookId: String): List<PromoJobDto> {
        if (!Auth.isLoggedIn) return emptyList()
        return try {
            val response = httpClient.get("$baseUrl/api/books/promo/jobs/$bookId")
            if (response.status.value != 200) return emptyList()
            json.decodeFromString<PromoJobsListResponse>(response.body<String>()).jobs
        } catch (e: Throwable) {
            if (e is kotlinx.coroutines.CancellationException) throw e
            emptyList()
        }
    }

    /**
     * Start a promo job: send the POST, parse the 202 reply into a
     * jobId. Centralises the «server rejected with 409/422/500»
     * error handling so the per-kind callers all behave the same.
     */
    private suspend fun startPromoJob(
        url: String,
        jsonBody: String? = null,
        errorLabel: String,
    ): String {
        // A failed fetch is a kotlin.Error in Ktor/JS: rethrow it as an
        // exception, the callers' `catch (e: Exception)` must see it.
        val (status, body) = try {
            val response = httpClient.post(url) {
                if (jsonBody != null) {
                    contentType(io.ktor.http.ContentType.Application.Json)
                    setBody(jsonBody)
                }
            }
            response.status.value to response.body<String>()
        } catch (e: Throwable) {
            if (e is Exception) throw e
            throw RuntimeException("$errorLabel failed (network)", e)
        }
        if (status !in 200..299) {
            val err = runCatching { json.decodeFromString<ApiErrorResponse>(body).error }.getOrNull()
            throw RuntimeException(err ?: "$errorLabel failed (HTTP $status)")
        }
        val dto = json.decodeFromString<PromoJobDto>(body)
        return dto.jobId ?: throw RuntimeException("$errorLabel start returned no jobId")
    }

    /**
     * Poll a promo job until it reaches a terminal state. Returns the
     * final DTO so the per-kind caller can decode the `result` subtree
     * into its expected type. Polling cadence (2s) is tuned for the
     * fastest promo kinds (annotation ~30s); after the first minute it
     * slows to 10s, since what's still running then (the full-book
     * batch scan) takes minutes to an hour. A failed fetch (network
     * drop, laptop sleep, 5xx) is retried without a limit: the job keeps
     * running server-side, and every way it can end — done, error, or the
     * server forgetting it (404) — is a definite answer once the server
     * is reachable again, so giving up would only strand the UI.
     */
    private suspend fun pollPromoJob(jobId: String): PromoJobDto {
        val startedAt = kotlin.js.Date.now()
        while (true) {
            // Ktor/JS reports a failed fetch as kotlin.Error, not Exception.
            val response = try {
                httpClient.get("$baseUrl/api/books/promo/jobs/job/$jobId").let { it.status.value to it.body<String>() }
            } catch (e: Throwable) {
                if (e is kotlinx.coroutines.CancellationException) throw e
                null
            }
            when {
                response == null || response.first >= 500 -> {}  // transient — poll again
                // Server forgot about the job (registry TTL elapsed or
                // Spring restarted). Caller falls back to fetching the
                // cached disk result via the existing GET endpoints.
                response.first == 404 -> throw RuntimeException("Job no longer tracked by server")
                response.first != 200 ->
                    throw RuntimeException("Job status fetch failed (HTTP ${response.first})")
                else -> {
                    val dto = json.decodeFromString<PromoJobDto>(response.second)
                    when (dto.status) {
                        "done" -> return dto
                        "error" -> throw RuntimeException(dto.error ?: "Generation failed")
                    }
                }
            }
            delay(if (kotlin.js.Date.now() - startedAt < 60_000) 2000 else 10_000)
        }
    }

    /**
     * Generic «poll then decode the result subtree» — shared by every
     * `awaitXJob` method so they're each a one-liner. The result
     * subtree is serialised back to a string and decoded with the
     * caller's serializer; this avoids dragging a generic dependency
     * on `JsonElement` decoding into every per-kind path.
     */
    private suspend fun <T> awaitAndDecode(
        jobId: String,
        deserializer: kotlinx.serialization.DeserializationStrategy<T>,
        errorLabel: String,
    ): T {
        val terminal = pollPromoJob(jobId)
        val resultElement = terminal.result
            ?: throw RuntimeException("$errorLabel job ended without a result")
        return json.decodeFromString(deserializer, resultElement.toString())
    }

    private suspend fun pollUntilDone(
        jobId: String,
        remainingCredits: Int?,
        onProgress: (status: PipelineStatusResponse) -> Unit
    ): PipelineResult {
        while (true) {
            val status = getPipelineStatus(jobId)
            onProgress(status)
            when (status.status) {
                // A `done` status with no report behind it is a backend
                // inconsistency, not an empty result — surface it instead of
                // letting a null slip into the caller's report slot.
                "done" -> return PipelineResult(
                    getPipelineResult(jobId)
                        ?: throw RuntimeException("Pipeline reported done but no report was found"),
                    remainingCredits,
                )
                "error" -> throw RuntimeException(status.error ?: "Pipeline failed")
            }
            delay(3000)
        }
    }
}

/**
 * Mirror of the backend's `promoJobDto` shape. `result` is a raw
 * `JsonElement` so the per-kind `awaitXJob` methods can decode it into
 * their target types without this DTO having to know all of them.
 */
@Serializable
data class PromoJobDto(
    val jobId: String? = null,
    val bookId: String? = null,
    /** "ANNOTATION" / "COVER_BATCH" / "COVER_REGEN" / "TEASER". */
    val kind: String? = null,
    /** Slot 1/2/3 for COVER_REGEN; null otherwise. */
    val variant: Int? = null,
    /** "batch" for an AI_HIGHLIGHTS_FULL batch run; null otherwise. */
    val mode: String? = null,
    /** Start time, epoch ms. */
    val startedAt: Double? = null,
    /** "running" / "done" / "error". */
    val status: String = "",
    val error: String? = null,
    val result: JsonElement? = null,
)

@Serializable
private data class PromoJobsListResponse(
    val jobs: List<PromoJobDto> = emptyList(),
)

@Serializable
data class BookHistoryItem(
    val fingerprint: String,
    val filename: String,
    val wordCount: Int = 0,
    val hasAiResult: Boolean = false,
    /** "idle" | "running" | "done" | "failed" — drives badge + auto-resume. */
    val aiStatus: String = "idle",
    /** Analyzer UUID for the running pipeline; null unless `aiStatus=running`. */
    val aiJobId: String? = null,
    /** True once the smart stylistic scan (preview or full) has been run — drives the «Smart» badge. */
    val hasSmartHighlights: Boolean = false,
    val analyzedAt: String = ""
)

@Serializable
data class PipelineStartResponse(
    val job_id: String? = null,
    val credits: Int? = null,
    val error: String? = null
)

@Serializable
data class AnnotationResult(
    val annotation: String = "",
    val hooks: List<String> = emptyList()
)

@Serializable
data class CoverRequest(
    val title: String,
    val author: String = "",
    val tagline: String = "",
    val prominence: String = "debut",
    val tone: String = "",
    /** "overlay" (default, SVG with vector text) or "baked"
     *  (text rendered into the image itself, returns PNG). */
    val textMode: String = "overlay",
)

@Serializable
private data class CoverBatchResponse(val covers: List<CoverResult> = emptyList())

@Serializable
data class CoverResult(
    val image: String = "",
    /** "svg" → image holds raw SVG XML; "png" → base64 PNG body. */
    val format: String = "svg",
    val variant: Int = 1,
    val prompt: String = "",
)

@Serializable
private data class CoverListResponse(val covers: List<CoverResult> = emptyList())

@Serializable
data class TeaserRequest(
    val title: String,
    val author: String = "",
    /** True → stop after keyframe (~$0.04, ~30s); false → full pipeline
     *  including i2v video (~$0.62, ~2 min). */
    val keyframeOnly: Boolean = false,
    /** True → re-run Sonnet to produce new teaser_prompt.json;
     *  false → reuse existing (saves ~$0.02 on re-renders). */
    val regeneratePrompts: Boolean = true,
    /** True → skip Gemini and reuse the existing teaser_frame1.png.
     *  Used to generate the video from a previewed keyframe without
     *  re-rendering the still. */
    val useExistingKeyframe: Boolean = false,
    /** Override the analyzer's auto-synthesised tone_register. */
    val toneOverride: String = "",
    /** Filename from style_references/ (e.g. cinematic_almodovar.jpeg)
     *  to override the LLM's auto-picked reference. */
    val styleReferenceOverride: String = "",
    val aspect: String = "16:9",
    val duration: Int = 5,
    /** Route i2v to the cheap fallback model and charge the
     *  discounted render price. */
    val lowQuality: Boolean = false,
    /** UI language tag — controls the language of the one user-facing
     *  field that Sonnet emits (chosen_element). Empty → repository
     *  injects appLocaleSnapshot() before sending. */
    val lang: String = "",
)

@Serializable
data class TeaserResult(
    /** Whether a keyframe PNG exists on disk for this book. The actual
     *  bytes are fetched via the Bearer-authed `/frame.png` endpoint
     *  and wrapped in a Blob URL on the client — no asset URL is ever
     *  exposed to the DOM. */
    val frame_exists: Boolean = false,
    /** Whether the rendered MP4 exists on disk. Same fetch-and-wrap
     *  pattern as the keyframe. */
    val video_exists: Boolean = false,
    /** Full teaser_prompt.json — scene/motion/style metadata for display.
     *  Nullable because the backend may emit null in partial-failure
     *  states (e.g. the Sonnet stage threw); kotlinx-serialization
     *  default-value fallback only kicks in for MISSING fields, not
     *  null ones, so the field has to admit null explicitly. */
    val teaser_prompt: TeaserPromptMeta? = null,
    val style_reference_used: String? = null,
    val cost: Double? = null,
    /** Cumulative count of scene-preview generations for this book.
     *  First two are free; the third onward costs 1 cr each. Drives the
     *  "Подобрать сцену" button price tag. */
    val preview_count: Int = 0,
)

@Serializable
data class TeaserPromptMeta(
    val tone_register: String = "",
    val chosen_element: String = "",
    val scene_prompt: String = "",
    val motion_prompt: String = "",
    val style_reference: String = "",
    val style_reasoning: String = "",
)

@Serializable
data class PipelineStatusResponse(
    val status: String = "",
    val stage: Int = 0,
    val stage_name: String = "",
    val total_stages: Int = 7,
    val error: String? = null,
    val chunk: Int = 0,
    val total_chunks: Int = 0,
    val eta_seconds: Int? = null,
    val elapsed_seconds: Int = 0
)
